/**
 * Cubing System Configuration
 * MapleStory-style potential system with percentage-based stat bonuses
 * Boss-only cube drops that reroll equipment potential lines
 */

// ============================================================
// CONFIGURATION
// ============================================================

export const CUBING_CONFIG = {
  // Number of potential lines per grade
  linesPerGrade: {
    rare: 2,
    epic: 3,
    unique: 3,
    legendary: 3,
  },

  // Grade order for progression
  gradeOrder: [null, 'rare', 'epic', 'unique', 'legendary'],

  // Grade display info
  gradeInfo: {
    rare: { name: 'Rare', color: '#3b82f6', icon: '🔵', shortCode: 'R' },
    epic: { name: 'Epic', color: '#8b5cf6', icon: '🟣', shortCode: 'E' },
    unique: { name: 'Unique', color: '#f59e0b', icon: '🟡', shortCode: 'U' },
    legendary: { name: 'Legendary', color: '#22c55e', icon: '🟢', shortCode: 'L' },
  },
};

// ============================================================
// CUBE TYPES (Boss Drops Only)
// ============================================================

export const CUBE_TYPES = {
  occultCube: {
    id: 'occultCube',
    name: 'Occult Cube',
    icon: '🎲',
    rarity: 'uncommon',
    description: 'Rerolls potential lines. Cannot upgrade grade.',
    isCube: true,
    stackSize: 99,
    gradeUpRates: { epic: 0, unique: 0, legendary: 0 },
    canGradeUp: false,
    sellValue: 50,
  },
  masterCube: {
    id: 'masterCube',
    name: 'Master Cube',
    icon: '💎',
    rarity: 'rare',
    description: 'Rerolls potential lines. Small chance to upgrade grade.',
    isCube: true,
    stackSize: 99,
    gradeUpRates: { epic: 0.05, unique: 0.01, legendary: 0 },
    canGradeUp: true,
    sellValue: 150,
  },
  meisterCube: {
    id: 'meisterCube',
    name: 'Meister Cube',
    icon: '🔮',
    rarity: 'epic',
    description: 'Rerolls potential lines. Good chance to upgrade grade.',
    isCube: true,
    stackSize: 99,
    gradeUpRates: { epic: 0.08, unique: 0.04, legendary: 0.005 },
    canGradeUp: true,
    sellValue: 350,
  },
  redCube: {
    id: 'redCube',
    name: 'Red Cube',
    icon: '❤️',
    rarity: 'epic',
    description: 'Rerolls potential lines. High chance to upgrade grade.',
    isCube: true,
    stackSize: 99,
    gradeUpRates: { epic: 0.10, unique: 0.06, legendary: 0.02 },
    canGradeUp: true,
    sellValue: 500,
  },
  blackCube: {
    id: 'blackCube',
    name: 'Black Cube',
    icon: '🖤',
    rarity: 'legendary',
    description: 'Best chance to upgrade. Can choose to keep current lines.',
    isCube: true,
    stackSize: 99,
    gradeUpRates: { epic: 0.15, unique: 0.10, legendary: 0.05 },
    canGradeUp: true,
    canKeepPrevious: true,
    sellValue: 1000,
  },
};

export const CUBE_IDS = ['occultCube', 'masterCube', 'meisterCube', 'redCube', 'blackCube'];

// ============================================================
// SLOT-BASED POTENTIAL STAT FILTERING
// Each equipment slot can only roll relevant stats
// ============================================================

export const SLOT_POTENTIAL_STATS = {
  weapon: ['STR', 'DEX', 'attack', 'critChance', 'critDamage', 'bossDamage', 'ignoreDef'],
  offhand: ['STR', 'DEX', 'defense', 'critChance', 'critDamage'],
  helmet: ['STR', 'INT', 'defense', 'health', 'allStats'],
  top: ['STR', 'DEX', 'INT', 'LUK', 'defense', 'health', 'allStats'],
  bottom: ['STR', 'DEX', 'INT', 'LUK', 'defense', 'health', 'allStats'],
  gloves: ['STR', 'DEX', 'attack', 'critChance', 'critDamage'],
  boots: ['STR', 'DEX', 'LUK', 'defense', 'health'],
  cape: ['STR', 'DEX', 'INT', 'LUK', 'allStats'],
  belt: ['STR', 'DEX', 'INT', 'LUK', 'health', 'allStats'],
  pendant: ['allStats', 'health', 'critChance', 'critDamage', 'bossDamage'],
  ring: ['allStats', 'attack', 'critChance', 'critDamage', 'bossDamage', 'ignoreDef'],
};

// ============================================================
// POTENTIAL LINE POOLS BY GRADE
// Each line has: stat, type ('percent' or 'flat'), value, weight
// ============================================================

export const POTENTIAL_LINES = {
  rare: [
    // Primary stats (percentage bonuses)
    { stat: 'STR', type: 'percent', value: 3, weight: 10 },
    { stat: 'DEX', type: 'percent', value: 3, weight: 10 },
    { stat: 'INT', type: 'percent', value: 3, weight: 10 },
    { stat: 'LUK', type: 'percent', value: 3, weight: 10 },
    // Combat stats (percentage bonuses)
    { stat: 'attack', type: 'percent', value: 3, weight: 8 },
    { stat: 'defense', type: 'percent', value: 3, weight: 8 },
    { stat: 'health', type: 'percent', value: 3, weight: 8 },
    // Flat bonuses
    { stat: 'critChance', type: 'flat', value: 0.02, weight: 6 },
    { stat: 'critDamage', type: 'flat', value: 0.05, weight: 6 },
  ],
  epic: [
    // Primary stats
    { stat: 'STR', type: 'percent', value: 6, weight: 10 },
    { stat: 'DEX', type: 'percent', value: 6, weight: 10 },
    { stat: 'INT', type: 'percent', value: 6, weight: 10 },
    { stat: 'LUK', type: 'percent', value: 6, weight: 10 },
    // Combat stats
    { stat: 'attack', type: 'percent', value: 6, weight: 8 },
    { stat: 'defense', type: 'percent', value: 6, weight: 8 },
    { stat: 'health', type: 'percent', value: 6, weight: 8 },
    // All Stats (special)
    { stat: 'allStats', type: 'percent', value: 3, weight: 4 },
    // Flat bonuses
    { stat: 'critChance', type: 'flat', value: 0.03, weight: 6 },
    { stat: 'critDamage', type: 'flat', value: 0.08, weight: 6 },
  ],
  unique: [
    // Primary stats
    { stat: 'STR', type: 'percent', value: 9, weight: 10 },
    { stat: 'DEX', type: 'percent', value: 9, weight: 10 },
    { stat: 'INT', type: 'percent', value: 9, weight: 10 },
    { stat: 'LUK', type: 'percent', value: 9, weight: 10 },
    // Combat stats
    { stat: 'attack', type: 'percent', value: 9, weight: 8 },
    { stat: 'defense', type: 'percent', value: 9, weight: 8 },
    { stat: 'health', type: 'percent', value: 9, weight: 8 },
    // All Stats
    { stat: 'allStats', type: 'percent', value: 6, weight: 4 },
    // Flat bonuses
    { stat: 'critChance', type: 'flat', value: 0.04, weight: 6 },
    { stat: 'critDamage', type: 'flat', value: 0.12, weight: 6 },
    // Special stats (Unique+)
    { stat: 'bossDamage', type: 'flat', value: 0.10, weight: 3 },
    { stat: 'ignoreDef', type: 'flat', value: 0.10, weight: 3 },
  ],
  legendary: [
    // Primary stats (high values)
    { stat: 'STR', type: 'percent', value: 12, weight: 10 },
    { stat: 'DEX', type: 'percent', value: 12, weight: 10 },
    { stat: 'INT', type: 'percent', value: 12, weight: 10 },
    { stat: 'LUK', type: 'percent', value: 12, weight: 10 },
    // Combat stats
    { stat: 'attack', type: 'percent', value: 12, weight: 8 },
    { stat: 'defense', type: 'percent', value: 12, weight: 8 },
    { stat: 'health', type: 'percent', value: 12, weight: 8 },
    // All Stats (high)
    { stat: 'allStats', type: 'percent', value: 9, weight: 4 },
    // Flat bonuses (high)
    { stat: 'critChance', type: 'flat', value: 0.05, weight: 6 },
    { stat: 'critDamage', type: 'flat', value: 0.15, weight: 6 },
    // Special stats (high values)
    { stat: 'bossDamage', type: 'flat', value: 0.20, weight: 3 },
    { stat: 'ignoreDef', type: 'flat', value: 0.15, weight: 3 },
  ],
};

// ============================================================
// CUBE DROP RATES BY BOSS TIER
// ============================================================

export const CUBE_DROPS = {
  tier1: [
    { cube: 'occultCube', chance: 1.0, minQty: 2, maxQty: 4 },
    { cube: 'masterCube', chance: 0.25, minQty: 1, maxQty: 2 },
  ],
  tier2: [
    { cube: 'occultCube', chance: 1.0, minQty: 3, maxQty: 5 },
    { cube: 'masterCube', chance: 0.60, minQty: 2, maxQty: 3 },
    { cube: 'meisterCube', chance: 0.15, minQty: 1, maxQty: 2 },
  ],
  tier3: [
    { cube: 'occultCube', chance: 1.0, minQty: 4, maxQty: 6 },
    { cube: 'masterCube', chance: 0.80, minQty: 3, maxQty: 4 },
    { cube: 'meisterCube', chance: 0.40, minQty: 2, maxQty: 3 },
    { cube: 'redCube', chance: 0.10, minQty: 1, maxQty: 2 },
  ],
  tier4: [
    { cube: 'occultCube', chance: 1.0, minQty: 5, maxQty: 8 },
    { cube: 'masterCube', chance: 1.0, minQty: 4, maxQty: 6 },
    { cube: 'meisterCube', chance: 0.70, minQty: 3, maxQty: 5 },
    { cube: 'redCube', chance: 0.35, minQty: 2, maxQty: 3 },
    { cube: 'blackCube', chance: 0.05, minQty: 1, maxQty: 1 },
  ],
  tier5: [
    { cube: 'occultCube', chance: 1.0, minQty: 6, maxQty: 10 },
    { cube: 'masterCube', chance: 1.0, minQty: 6, maxQty: 8 },
    { cube: 'meisterCube', chance: 1.0, minQty: 5, maxQty: 7 },
    { cube: 'redCube', chance: 0.60, minQty: 3, maxQty: 5 },
    { cube: 'blackCube', chance: 0.20, minQty: 1, maxQty: 2 },
  ],
};

// ============================================================
// HELPER FUNCTIONS
// ============================================================

/**
 * Get the grade info for display
 * @param {string} grade - Potential grade
 * @returns {Object} Grade display info
 */
export function getGradeInfo(grade) {
  return CUBING_CONFIG.gradeInfo[grade] || { name: 'None', color: '#6b7280', icon: '⚪', shortCode: '--' };
}

/**
 * Get the next grade in progression
 * @param {string} currentGrade - Current potential grade
 * @returns {string|null} Next grade or null if at max
 */
export function getNextGrade(currentGrade) {
  const order = CUBING_CONFIG.gradeOrder;
  const currentIndex = order.indexOf(currentGrade);
  if (currentIndex === -1 || currentIndex >= order.length - 1) return null;
  return order[currentIndex + 1];
}

/**
 * Format a potential line for display
 * @param {Object} line - Potential line object
 * @returns {string} Formatted string like "+6% STR" or "+3% Crit"
 */
export function formatPotentialLine(line) {
  const statLabels = {
    STR: 'STR',
    DEX: 'DEX',
    INT: 'INT',
    LUK: 'LUK',
    attack: 'ATK',
    defense: 'DEF',
    health: 'HP',
    allStats: 'All Stats',
    critChance: 'Crit Chance',
    critDamage: 'Crit Damage',
    bossDamage: 'Boss Damage',
    ignoreDef: 'Ignore DEF',
  };

  const label = statLabels[line.stat] || line.stat;

  if (line.type === 'percent') {
    return `+${line.value}% ${label}`;
  } else {
    // Flat bonuses (for crit stats, show as percentage)
    if (line.stat === 'critChance' || line.stat === 'critDamage' || line.stat === 'bossDamage' || line.stat === 'ignoreDef') {
      return `+${Math.round(line.value * 100)}% ${label}`;
    }
    return `+${line.value} ${label}`;
  }
}

/**
 * Get cube type definition
 * @param {string} cubeId - Cube ID
 * @returns {Object|null} Cube definition
 */
export function getCubeType(cubeId) {
  return CUBE_TYPES[cubeId] || null;
}

/**
 * Get potential line pool for a grade
 * @param {string} grade - Potential grade
 * @returns {Array} Array of potential lines
 */
export function getLinePool(grade) {
  return POTENTIAL_LINES[grade] || [];
}

/**
 * Get number of lines for a grade
 * @param {string} grade - Potential grade
 * @returns {number} Number of lines
 */
export function getLinesForGrade(grade) {
  return CUBING_CONFIG.linesPerGrade[grade] || 0;
}

export default {
  CUBING_CONFIG,
  CUBE_TYPES,
  CUBE_IDS,
  SLOT_POTENTIAL_STATS,
  POTENTIAL_LINES,
  CUBE_DROPS,
  getGradeInfo,
  getNextGrade,
  formatPotentialLine,
  getCubeType,
  getLinePool,
  getLinesForGrade,
};
